/**
 * Auth Hook
 * =========
 * React Query hook for authentication state.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/services/api'
import type { AuthStatus } from '@/types'

/**
 * Hook for checking and managing authentication status.
 */
export function useAuth() {
  const queryClient = useQueryClient()

  // Query for auth status
  const {
    data: authStatus,
    isLoading,
    error,
    refetch,
  } = useQuery<AuthStatus>({
    queryKey: ['auth', 'status'],
    queryFn: authApi.status,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: false,
  })

  // Mutation for logout
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // Clear auth status and refetch
      queryClient.setQueryData(['auth', 'status'], {
        authenticated: false,
        user: null,
      })
      // Invalidate all queries on logout
      queryClient.invalidateQueries()
    },
  })

  return {
    // State
    isAuthenticated: authStatus?.authenticated ?? false,
    user: authStatus?.user ?? null,
    isLoading,
    error,

    // Actions
    login: (redirectUrl?: string) => {
      window.location.href = authApi.getLoginUrl(redirectUrl)
    },
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
    refetch,
  }
}
